import { API_BASE_URL } from "../../constants";
import type {
  StripeAccountStatus,
  StripeAccountStatusCode,
  Step2Form,
  SellerType,
  AccountDetailsPrefill,
} from "./seller-setup-types";

/** Raw account_details from backend (snake_case, from Stripe). */
interface RawAccountDetails {
  step1?: {
    account_type?: string;
    country?: string;
    email?: string;
    industry?: string;
    business_website?: string;
  };
  step2_individual?: Record<string, unknown>;
  step2_business?: Record<string, unknown>;
  step3?: { bank_account_connected?: boolean };
}

/** Raw status response from backend (snake_case). */
interface StripeAccountStatusResponse {
  success?: boolean;
  stripeAccountId?: string | null;
  onboarding_step?: number | null;
  onboardingStep?: number | null;
  account_details?: RawAccountDetails | null;
  accountDetails?: AccountDetailsPrefill | null;
  payouts_enabled?: boolean;
  payoutsEnabled?: boolean;
  details_submitted?: boolean;
  status?: StripeAccountStatusCode;
  requirements?: {
    currently_due?: string[];
    past_due?: string[];
    pending_verification?: string[];
    currentlyDue?: string[];
    pastDue?: string[];
    pendingVerification?: string[];
  };
  disabled_reason?: string | null;
  disabledReason?: string | null;
}

function normalizeAccountDetails(raw: RawAccountDetails | undefined | null): AccountDetailsPrefill | undefined {
  if (!raw) return undefined;
  const step1 = raw.step1
    ? {
        accountType: (raw.step1.account_type === "company" ? "business" : raw.step1.account_type === "individual" ? "individual" : undefined) as "individual" | "business" | undefined,
        country: raw.step1.country ?? undefined,
        email: raw.step1.email ?? undefined,
        industry: raw.step1.industry ?? undefined,
        businessWebsite: raw.step1.business_website ?? undefined,
      }
    : undefined;
  const si = raw.step2_individual as { first_name?: string; last_name?: string; dob?: string; phone?: string; address?: { line1?: string; city?: string; state?: string; postal_code?: string } } | undefined;
  const step2Individual = si
    ? {
        firstName: si.first_name ?? undefined,
        lastName: si.last_name ?? undefined,
        dob: si.dob ?? undefined,
        phone: si.phone ?? undefined,
        address: si.address ? { line1: si.address.line1 ?? "", city: si.address.city ?? "", state: si.address.state ?? "", postalCode: si.address.postal_code ?? "" } : undefined,
      }
    : undefined;
  const sb = raw.step2_business as { business_name?: string; business_phone?: string; tax_id?: string; business_address?: { line1?: string; city?: string; state?: string; postal_code?: string }; representative?: Record<string, unknown> } | undefined;
  const step2Business = sb
    ? {
        businessName: sb.business_name ?? undefined,
        businessPhone: sb.business_phone ?? undefined,
        taxId: sb.tax_id ?? undefined,
        businessAddress: sb.business_address ? { line1: sb.business_address.line1 ?? "", city: sb.business_address.city ?? "", state: sb.business_address.state ?? "", postalCode: sb.business_address.postal_code ?? "" } : undefined,
        representative: (() => {
          const rep = (raw.step2_business as { representative?: { first_name?: string; last_name?: string; dob?: string; phone?: string; address?: { line1?: string; city?: string; state?: string; postal_code?: string } } }).representative;
          if (!rep) return undefined;
          const addr = rep.address;
          return {
            firstName: rep.first_name ?? "",
            lastName: rep.last_name ?? "",
            dob: rep.dob ?? "",
            phone: rep.phone ?? "",
            address: addr ? { line1: addr.line1 ?? "", city: addr.city ?? "", state: addr.state ?? "", postalCode: addr.postal_code ?? "" } : { line1: "", city: "", state: "", postalCode: "" },
          };
        })(),
      }
    : undefined;
  const step3 = raw.step3
    ? { bankAccountConnected: !!raw.step3.bank_account_connected }
    : undefined;
  if (!step1 && !step2Individual && !step2Business && !step3) return undefined;
  return { step1, step2Individual, step2Business, step3 };
}

function normalizeStripeStatus(raw: StripeAccountStatusResponse): StripeAccountStatus {
  const req = raw.requirements ?? {};
  const onboardingStep = raw.onboardingStep ?? raw.onboarding_step ?? null;
  const accountDetails =
    raw.accountDetails ??
    (raw.account_details ? normalizeAccountDetails(raw.account_details) : undefined);
  return {
    stripeAccountId: raw.stripeAccountId ?? null,
    onboardingStep: onboardingStep != null && onboardingStep >= 1 && onboardingStep <= 5 ? onboardingStep : null,
    accountDetails: accountDetails ?? null,
    payoutsEnabled: raw.payouts_enabled ?? raw.payoutsEnabled ?? false,
    status: raw.status ?? "PENDING",
    requirements: {
      currentlyDue: req.currently_due ?? req.currentlyDue ?? [],
      pastDue: req.past_due ?? req.pastDue ?? [],
      pendingVerification: req.pending_verification ?? req.pendingVerification ?? [],
    },
    disabledReason: raw.disabled_reason ?? raw.disabledReason ?? null,
  };
}


const BASE_URL = `${API_BASE_URL}/app/api`;

/** Stripe publishable key — safe for frontend. Set in .env as REACT_APP_STRIPE_PUBLISHABLE_KEY. */
export const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

function getAuthHeaders(profileId: string | number): HeadersInit {
  return {
    "Content-Type": "application/json",
    "X-Profile-Id": String(profileId),
  };
}

/**
 * Create Stripe Connect account (Step 1).
 * Your backend must create the account with Stripe's API (secret key server-side) and store stripeAccountId on the user.
 */
export async function createStripeConnectAccount(
  profileId: string | number,
  payload: { email: string; accountType: SellerType; country: string; profileId: string | number; industry: string; businessWebsite: string }
): Promise<{ stripeAccountId: string }> {
  const response = await fetch(`${BASE_URL}/seller-setup/connect-account`, {
    method: "POST",
    headers: getAuthHeaders(profileId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to create seller account");
  }
  return response.json();
}

/**
 * Update Step 1 details on an existing Stripe Connect account (e.g. switching accountType
 * after going back). createStripeConnectAccount() is a no-op once stripeAccountId already
 * exists, so changes to account type/country/etc. must go through this PUT instead.
 */
export async function updateStripeConnectAccountStep1(
  profileId: string | number,
  payload: { email: string; accountType: SellerType; country: string; industry: string; businessWebsite: string }
): Promise<void> {
  const response = await fetch(`${BASE_URL}/seller-setup/connect-account`, {
    method: "PUT",
    headers: getAuthHeaders(profileId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to update seller account");
  }
}

/**
 * Fetch Stripe Connect account status.
 * Normalizes backend response (snake_case: payouts_enabled, currently_due, past_due, disabled_reason) to our camelCase type.
 */
export async function getStripeConnectAccountStatus(
  profileId: string | number,
  returnFromStripe?: boolean,
): Promise<StripeAccountStatus | null> {
  const params = new URLSearchParams({ profileId: String(profileId) });
  if (returnFromStripe) params.set("returnFromStripe", "true");
  const response = await fetch(
    `${BASE_URL}/seller-setup/connect-account/status?${params}`,
    { headers: getAuthHeaders(profileId) }
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to load account status");
  }
  const raw: StripeAccountStatusResponse = await response.json();
  return normalizeStripeStatus(raw);
}

/**
 * Submit step 2 (legal identity). Backend updates Stripe; do not store SSN/sensitive data in your DB.
 */
export async function updateStripeConnectAccountStep2(
  profileId: string | number,
  payload: Step2Form & { accountType: SellerType }
): Promise<void> {
  const response = await fetch(`${BASE_URL}/seller-setup/connect-account/identity`, {
    method: "PUT",
    headers: getAuthHeaders(profileId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to save legal details");
  }
}

/**
 * Step 3 — payout setup. Sends Stripe bank account token (btok_xxx) to backend.
 * Backend attaches it to the Connect account with stripe.accounts.createExternalAccount(connectAccountId, { external_account: payload.externalAccountToken }).
 * Never send raw routing/account numbers — only the token from Stripe.js.
 */
export async function updateStripeConnectAccountStep3(
  profileId: string | number,
  payload: { externalAccountToken: string; accountHolderName?: string; accountHolderType?: "individual" | "company"; ssn_last_4?: string }
): Promise<void> {
  const response = await fetch(`${BASE_URL}/seller-setup/connect-account/payout`, {
    method: "PUT",
    headers: getAuthHeaders(profileId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to save payout details");
  }
}

/**
 * Submit for verification (Step 4). Sends ToS acceptance timestamp so backend can record it with Stripe.
 * Backend must call: stripe.accounts.update(connectAccountId, { tos_acceptance: { date, ip } })
 * so Stripe clears "Accept terms of service" (see BACKEND_TOS_ACCEPTANCE.md).
 */
export async function submitStripeConnectVerification(
  profileId: string | number
): Promise<void> {
  const response = await fetch(`${BASE_URL}/seller-setup/connect-account/submit`, {
    method: "POST",
    headers: getAuthHeaders(profileId),
    body: JSON.stringify({
      tos_acceptance: {
        date: Math.floor(Date.now() / 1000),
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      },
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to submit for verification");
  }
}

/**
 * Create an Account Link to complete verification on Stripe's hosted page.
 * Use when legal guardian or other requirements need Stripe's hosted flow (e.g. minor accounts).
 */
export async function createStripeAccountLink(
  profileId: string | number,
  returnUrl: string,
  refreshUrl?: string
): Promise<{ url: string }> {
  const response = await fetch(`${BASE_URL}/seller-setup/connect-account/account-link`, {
    method: "POST",
    headers: getAuthHeaders(profileId),
    body: JSON.stringify({
      return_url: returnUrl,
      refresh_url: refreshUrl || returnUrl,
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to create verification link");
  }
  return response.json();
}
